import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Button, Form, Modal, Badge, Spinner, InputGroup, Tabs, Tab, ListGroup } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaUpload, FaImage, FaPlayCircle, FaStopCircle, FaRegClock, FaList, FaUser, FaMagic, FaSearch, FaCheck, FaUndo, FaHistory } from "react-icons/fa";

// Time helpers
const formatForInput = (unixTimestamp) => {
  if (!unixTimestamp) return "";
  const d = new Date(unixTimestamp * 1000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const parseFromInput = (localString) => {
  if (!localString) return 0;
  return Math.floor(new Date(localString).getTime() / 1000);
};

const formatDateNice = (unixTimestamp) => {
  if (!unixTimestamp) return "N/A";
  return new Date(unixTimestamp * 1000).toLocaleString(undefined, { 
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' 
  });
};

// Image Helper: Converts saved filename string to full URL
const getImageUrl = (filename, fallbackId = null) => {
  let name = filename || fallbackId;
  if (!name) return "";
  name = name.toString().replace(/\.svg$/i, ''); // Strip ext just in case
  return `/images/contest/sketch/${name}.svg`;
};

export default function ContestManager() {
  const [contests, setContests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [premadeImages, setPremadeImages] = useState([]);
  const [modIdeas, setModIdeas] = useState([]);
  const [pastThemes, setPastThemes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  
  // Image Selector Modal State
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageTarget, setImageTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("premade");

  // Past Themes Selector Modal State
  const [showPastThemesModal, setShowPastThemesModal] = useState(false);
  const [pastThemeTarget, setPastThemeTarget] = useState(null);
  const [pastThemeSearch, setPastThemeSearch] = useState("");

  // Game-Icons Search State
  const [giSearch, setGiSearch] = useState("");
  const [giResults, setGiResults] = useState([]);
  const [isSearchingGi, setIsSearchingGi] = useState(false);
  const [isDownloadingGi, setIsDownloadingGi] = useState(false);

  const [newIdeaText, setNewIdeaText] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", short: "", idea: "", image: "",
    start_date: "", submissions_end_date: "", end_date: "",
  });
  
  const [themes, setThemes] = useState([
    { theme: "", image: "" }, { theme: "", image: "" }, { theme: "", image: "" },
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [contestsRes, suggestionsRes, imagesRes, modIdeasRes, pastThemesRes] = await Promise.all([
        axios.get("/api/moderation/get_contests.php"),
        axios.get("/api/moderation/get_suggestions.php"),
        axios.get("/api/moderation/get_premade_images.php"),
        axios.get("/api/moderation/get_mod_ideas.php"),
        axios.get("/api/moderation/get_past_themes.php") // Fetch our new themes API
      ]);
      setContests(contestsRes.data);
      setSuggestions(suggestionsRes.data);
      setPremadeImages(imagesRes.data);
      setModIdeas(modIdeasRes.data);
      setPastThemes(pastThemesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    if (!silent) setLoading(false);
  };

  // Dedicated lightweight fetch just for the notepad
  const fetchModIdeas = async () => {
    try {
      const res = await axios.get("/api/moderation/get_mod_ideas.php");
      setModIdeas(res.data);
    } catch (err) {
      console.error("Error fetching mod ideas", err);
    }
  };

  const getPhase = (contest) => {
    const now = Date.now() / 1000;
    if (now < contest.start_date) return { text: "Upcoming", bg: "secondary" };
    if (now >= contest.start_date && now < contest.submissions_end_date) return { text: "Submission Phase", bg: "success" };
    if (now >= contest.submissions_end_date && now < contest.end_date) return { text: "Voting Phase", bg: "warning", textDark: true };
    return { text: "Ended", bg: "danger" };
  };

  const handleOpenForm = (contest = null) => {
    if (contest) {
      setEditingId(contest.id);
      setFormData({
        title: contest.title || "",
        description: contest.description || "",
        short: contest.short || "",
        idea: contest.idea || "",
        image: contest.image || "",
        start_date: formatForInput(contest.start_date),
        submissions_end_date: formatForInput(contest.submissions_end_date),
        end_date: formatForInput(contest.end_date),
      });
      
      const existingThemes = contest.themes || [];
      const newThemes = [0, 1, 2].map(i => ({
        theme: existingThemes[i]?.theme || "",
        image: existingThemes[i]?.image || "",
      }));
      setThemes(newThemes);
    } else {
      setEditingId(null);
      setFormData({
        title: "", description: "", short: "", idea: "", image: "",
        start_date: "", submissions_end_date: "", end_date: ""
      });
      setThemes([{ theme: "", image: "" }, { theme: "", image: "" }, { theme: "", image: "" }]);
    }
    setShowFormModal(true);
  };

  // Starts a new contest using data directly from a theme
  const handleStartFromTheme = (themeText, themeImage) => {
    setEditingId(null); 
    setFormData({
      title: "", // Leaves title blank for user to input
      description: themeText || "",
      short: themeText || "",
      idea: "", 
      image: themeImage || "",
      start_date: "", 
      submissions_end_date: "", 
      end_date: ""
    });
    setThemes([{ theme: "", image: "" }, { theme: "", image: "" }, { theme: "", image: "" }]);
    setShowFormModal(true);
  };

  const handleAutofillDates = () => {
    let startMs;
    if (formData.start_date) {
      startMs = new Date(formData.start_date).getTime();
    } else {
      if (contests.length > 0) {
        const latestEndUnix = Math.max(...contests.map(c => c.end_date));
        startMs = latestEndUnix * 1000;
      } else {
        startMs = Date.now();
      }
    }
    
    const subEndMs = startMs + (5 * 24 * 60 * 60 * 1000);
    const endMs = startMs + (7 * 24 * 60 * 60 * 1000);

    setFormData(prev => ({
      ...prev,
      start_date: formatForInput(startMs / 1000),
      submissions_end_date: formatForInput(subEndMs / 1000),
      end_date: formatForInput(endMs / 1000)
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contest?")) return;
    try {
      await axios.post("/api/moderation/delete_contest.php", { id });
      fetchData(true);
    } catch (error) {
      console.error("Error deleting contest:", error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id: editingId,
        ...formData,
        start_date: parseFromInput(formData.start_date),
        submissions_end_date: parseFromInput(formData.submissions_end_date),
        end_date: parseFromInput(formData.end_date),
        dropShadow: "light",
        themes: themes
      };

      await axios.post("/api/moderation/save_contest.php", payload);
      setShowFormModal(false);
      fetchData(true);
    } catch (error) {
      console.error("Error saving contest:", error);
    }
  };

  // Image Modal Handlers
  const openImageSelector = (target) => {
    setImageTarget(target);
    setShowImageModal(true);
  };

  const applyImageSelection = (filename) => {
    if (imageTarget === 'main') {
      setFormData(prev => ({ ...prev, image: filename }));
    } else if (imageTarget !== null) {
      const newThemes = [...themes];
      newThemes[imageTarget].image = filename;
      setThemes(newThemes);
    }
    setShowImageModal(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);

    try {
      const res = await axios.post("/api/moderation/upload_image.php", fd);
      if (res.data.success) {
        applyImageSelection(res.data.filename);
      } else {
        alert(res.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed", error);
    }
  };

  const handleSearchGi = async (e) => {
    e.preventDefault();
    if (!giSearch) return;
    setIsSearchingGi(true);
    try {
      const res = await axios.get(`https://api.iconify.design/search?query=${giSearch}&prefix=game-icons&limit=100`);
      setGiResults(res.data.icons || []);
    } catch (err) {
      console.error(err);
    }
    setIsSearchingGi(false);
  };

  const handleGameIconSelect = async (iconId) => {
    setIsDownloadingGi(true);
    try {
      const res = await axios.post("/api/moderation/download_game_icon.php", { icon_id: iconId });
      if (res.data.success) {
        applyImageSelection(res.data.filename);
      } else {
        alert(res.data.message || "Failed to download game icon.");
      }
    } catch (err) {
      console.error(err);
      alert("Error contacting server to download icon.");
    }
    setIsDownloadingGi(false);
  };

  // Past Themes Handlers
  const openPastThemesModal = (targetIndex) => {
    setPastThemeTarget(targetIndex);
    setPastThemeSearch("");
    setShowPastThemesModal(true);
  };

  const handleSelectPastTheme = (themeObj) => {
    if (pastThemeTarget !== null) {
      const newThemes = [...themes];
      newThemes[pastThemeTarget].theme = themeObj.theme;
      newThemes[pastThemeTarget].image = themeObj.image;
      setThemes(newThemes);
    }
    setShowPastThemesModal(false);
  };

  const filteredPastThemes = pastThemes.filter(pt => 
    pt.theme.toLowerCase().includes(pastThemeSearch.toLowerCase())
  );


  // Mod Notepad Handlers
  const handleAddIdea = async (e) => {
    e.preventDefault();
    if (!newIdeaText.trim()) return;
    try {
      await axios.post("/api/moderation/add_mod_idea.php", { idea_text: newIdeaText });
      setNewIdeaText("");
      fetchModIdeas();
    } catch (err) {
      console.error("Error adding idea", err);
    }
  };

  const handleToggleIdea = async (id, currentStatus) => {
    setModIdeas(prev => prev.map(idea => idea.id === id ? { ...idea, is_used: !currentStatus } : idea));
    try {
      await axios.post("/api/moderation/toggle_mod_idea.php", { id, is_used: !currentStatus });
    } catch (err) {
      console.error("Error toggling idea", err);
      fetchModIdeas();
    }
  };

  const handleDeleteIdea = async (id) => {
    if (!window.confirm("Delete this idea entirely?")) return;
    setModIdeas(prev => prev.filter(idea => idea.id !== id));
    try {
      await axios.post("/api/moderation/delete_mod_idea.php", { id });
    } catch (err) {
      console.error("Error deleting idea", err);
      fetchModIdeas();
    }
  };

  const activeIdeas = modIdeas.filter(i => !i.is_used);
  const usedIdeas = modIdeas.filter(i => i.is_used);

  const nowMs = Date.now();
  const sevenDaysAgo = nowMs - (7 * 24 * 60 * 60 * 1000);
  const recentSuggestions = suggestions.filter((s, idx) => {
    if (idx === 0) return true;
    return new Date(s.created_at).getTime() >= sevenDaysAgo;
  });

  if (loading) return <Container className="text-center mt-5"><Spinner animation="border" /></Container>;

  return (
    <Container className="py-4" style={{ maxWidth: '1000px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Contest Management</h2>
        <Button variant="primary" onClick={() => handleOpenForm()}><FaPlus /></Button>
      </div>

      {suggestions.length > 0 && (
        <div className="bg-light border rounded p-3 mb-4 shadow-sm">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h5 className="mb-0 text-success">Recent Winner Suggestions</h5>
            <Button variant="outline-success" size="sm" onClick={() => setShowSuggestionsModal(true)}>
              <FaList className="me-1" /> View All
            </Button>
          </div>
          <div className="d-flex flex-column gap-2 mt-3">
            {recentSuggestions.map(s => (
              <div key={s.id} className="d-flex align-items-center bg-white border p-2 rounded">
                <Badge bg="success" className="me-3">User {s.winner_user_id}</Badge>
                <div className="fw-bold flex-grow-1">{s.suggested_theme}</div>
                <small className="text-muted ms-3">{s.created_at}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      <h4 className="mb-3">Active & Future Contests</h4>
      {contests.length === 0 && <p className="text-muted">No active or future contests found.</p>}
      
      {contests.map((contest) => {
        const phase = getPhase(contest);
        const displayImageUrl = getImageUrl(contest.image, contest.id);

        return (
          <div key={contest.id} className="d-flex flex-column flex-md-row bg-white rounded shadow-sm border overflow-hidden mb-4">
            <div 
              className="bg-light p-3 d-flex flex-row flex-md-column align-items-center justify-content-center gap-3 border-bottom border-md-end border-md-bottom-0" 
              style={{ minWidth: '200px' }}
            >
              <img 
                src={displayImageUrl} 
                alt="Contest" 
                style={{ width: '100%', maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }} 
              />
              <div className="d-flex align-items-center justify-content-center bg-white border px-3 py-1 rounded shadow-sm w-100 text-center">
                <FaUser className="text-primary me-2" />
                <span className="fw-bold small">{contest.submission_count || 0} Subs</span>
              </div>
            </div>

            <div className="p-4 flex-grow-1">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="mb-0">{contest.title}</h4>
                  <Badge bg={phase.bg} text={phase.textDark ? "dark" : "light"}>{phase.text}</Badge>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={() => handleOpenForm(contest)}>
                    <FaEdit /> Edit
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(contest.id)}>
                    <FaTrash />
                  </Button>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-4 text-muted small mb-3 border-bottom pb-3">
                <div><FaPlayCircle className="text-success me-1"/> <strong>Start:</strong> {formatDateNice(contest.start_date)}</div>
                <div><FaRegClock className="text-warning me-1"/> <strong>Subs End:</strong> {formatDateNice(contest.submissions_end_date)}</div>
                <div><FaStopCircle className="text-danger me-1"/> <strong>End:</strong> {formatDateNice(contest.end_date)}</div>
              </div>

              <div className="mb-3 text-secondary" dangerouslySetInnerHTML={{ __html: contest.short }}></div>

              {contest.themes && contest.themes.length > 0 && (
                <div className="bg-light p-2 rounded">
                  <span className="small fw-bold text-muted d-block mb-2">Next Week Themes:</span>
                  <div className="d-flex flex-wrap gap-3">
                    {contest.themes.map((t, i) => (
                      <div 
                        key={i} 
                        className="d-flex align-items-center bg-white border rounded px-2 py-1 flex-fill shadow-sm"
                        style={{ cursor: 'pointer', transition: '0.2s' }}
                        title="Click to start a new contest from this theme"
                        onClick={() => handleStartFromTheme(t.theme, t.image)}
                        onMouseOver={(e) => {
                          e.currentTarget.classList.replace('bg-white', 'bg-light');
                          e.currentTarget.classList.add('border-primary');
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.classList.replace('bg-light', 'bg-white');
                          e.currentTarget.classList.remove('border-primary');
                        }}
                      >
                        {t.image && <img src={getImageUrl(t.image)} alt="t" width="24" height="24" className="me-2 rounded" style={{ objectFit: 'cover' }} />}
                        <span style={{ fontSize: '0.85rem' }} className="fw-bold me-auto text-primary">{t.theme || "No Title"}</span>
                        <Badge bg="secondary" className="ms-2">{t.vote_count || 0}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ================= MODERATOR NOTEPAD ================= */}
      <hr className="my-5" />
      <h4 className="mb-3">Ideas Notepad</h4>
      <div className="bg-white border rounded shadow-sm p-4 mb-5">
        <Form onSubmit={handleAddIdea} className="mb-4">
          <InputGroup>
            <Form.Control 
              placeholder="Ideas for a future contest..." 
              value={newIdeaText}
              onChange={(e) => setNewIdeaText(e.target.value)}
            />
            <Button type="submit" variant="primary">
              <FaPlus className="me-1"/> Add Idea
            </Button>
          </InputGroup>
        </Form>

        <Tabs defaultActiveKey="active" className="mb-3">
          <Tab eventKey="active" title={`Active Ideas (${activeIdeas.length})`}>
            {activeIdeas.length === 0 ? <p className="text-muted">No active ideas right now.</p> : (
              <ListGroup variant="flush">
                {activeIdeas.map(idea => (
                  <ListGroup.Item key={idea.id} className="d-flex justify-content-between align-items-center px-0">
                    <div>
                      <span>{idea.idea_text}</span>
                      <div className="text-muted small mt-1">Added: {idea.created_at}</div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-success" size="sm" onClick={() => handleToggleIdea(idea.id, idea.is_used)} title="Mark as Used">
                        <FaCheck />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteIdea(idea.id)} title="Delete">
                        <FaTrash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Tab>

          <Tab eventKey="used" title={`Used Ideas (${usedIdeas.length})`}>
            {usedIdeas.length === 0 ? <p className="text-muted">No used ideas yet.</p> : (
              <ListGroup variant="flush">
                {usedIdeas.map(idea => (
                  <ListGroup.Item key={idea.id} className="d-flex justify-content-between align-items-center px-0 bg-light text-muted">
                    <div style={{ textDecoration: 'line-through' }}>
                      {idea.idea_text}
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => handleToggleIdea(idea.id, idea.is_used)} title="Move back to Active">
                        <FaUndo />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteIdea(idea.id)} title="Delete">
                        <FaTrash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Tab>
        </Tabs>
      </div>

      {/* Main Edit/Create Form Modal */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg" backdrop="static">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>{editingId ? "Edit Contest" : "Make New Contest"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Title</Form.Label>
                  <Form.Control required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Description (Full)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Short Description (Tooltips on Cards)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={formData.short} onChange={e => setFormData({...formData, short: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Idea By (User ID)</Form.Label>
                  <Form.Control type="number" value={formData.idea} onChange={e => setFormData({...formData, idea: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Main Contest Image</Form.Label>
                  <InputGroup>
                    <Form.Control readOnly value={formData.image} placeholder="" />
                    <Button variant="outline-secondary" onClick={() => openImageSelector('main')}>
                      <FaImage className="me-1" /> Select/Upload
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4 bg-light p-3 rounded border mx-0 align-items-end">
              <Col md={4}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="small fw-bold mb-0">Start Date</Form.Label>
                    <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={handleAutofillDates} title="Auto-calc from start, or grabs last contest's end date">
                      <FaMagic className="me-1" /> Autofill
                    </Button>
                  </div>
                  <Form.Control type="datetime-local" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">Submissions End</Form.Label>
                  <Form.Control type="datetime-local" required value={formData.submissions_end_date} onChange={e => setFormData({...formData, submissions_end_date: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">End Date</Form.Label>
                  <Form.Control type="datetime-local" required value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <h5 className="mb-3">Next Week Theme Voting Slots</h5>
            {themes.map((t, i) => (
              <Row key={i} className="mb-3 align-items-end border-bottom pb-3 mx-0">
                <Col md={8} className="ps-0">
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-end mb-1">
                      <Form.Label className="small text-muted mb-0">Theme {i + 1} Text</Form.Label>
                      <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => openPastThemesModal(i)}>
                        <FaHistory className="me-1" /> Reuse Past
                      </Button>
                    </div>
                    <Form.Control 
                      value={t.theme} 
                      onChange={e => {
                        const newT = [...themes]; newT[i].theme = e.target.value; setThemes(newT);
                      }} 
                      placeholder="Theme idea..."
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="pe-0">
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Image</Form.Label>
                    <InputGroup>
                      <Form.Control readOnly value={t.image} placeholder="No image selected" />
                      <Button variant="outline-secondary" onClick={() => openImageSelector(i)}>
                        <FaImage /> Select
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFormModal(false)}>Cancel</Button>
            <Button variant="success" type="submit">Save Contest</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Past Themes Selector Modal */}
      <Modal show={showPastThemesModal} onHide={() => setShowPastThemesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reuse Past Theme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control 
            className="mb-3"
            placeholder="Search past themes..."
            value={pastThemeSearch}
            onChange={e => setPastThemeSearch(e.target.value)}
          />
          <div className="d-flex flex-column gap-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredPastThemes.length === 0 && <p className="text-muted w-100 text-center mt-3">No matching past themes found.</p>}
            {filteredPastThemes.map((pt, idx) => (
              <div 
                key={idx} 
                className="d-flex align-items-center border rounded p-2 bg-light shadow-sm"
                style={{ cursor: 'pointer', transition: '0.2s' }}
                onClick={() => handleSelectPastTheme(pt)}
                onMouseOver={(e) => {
                  e.currentTarget.classList.replace('bg-light', 'bg-white');
                  e.currentTarget.classList.add('border-primary');
                }}
                onMouseOut={(e) => {
                  e.currentTarget.classList.replace('bg-white', 'bg-light');
                  e.currentTarget.classList.remove('border-primary');
                }}
              >
                {pt.image ? (
                  <img src={getImageUrl(pt.image)} alt="t" width="40" height="40" className="me-3 rounded bg-white border p-1" style={{ objectFit: 'contain' }} onError={(e) => e.target.style.display='none'} />
                ) : (
                  <div style={{ width: 40, height: 40 }} className="me-3 bg-secondary rounded d-flex justify-content-center align-items-center text-white small">N/A</div>
                )}
                <div className="fw-bold">{pt.theme}</div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Image Selector Modal with Tabs */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select or Upload SVG Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3 px-3 pt-3"
          >
            <Tab eventKey="premade" title="Premade History">
              <div className="px-3 pb-3">
                <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {premadeImages.length === 0 && <p className="text-muted w-100 text-center">No premade images found.</p>}
                  {premadeImages.map((imgStr, idx) => (
                    <div 
                      key={idx} 
                      className="border rounded p-1" 
                      style={{ width: '80px', height: '80px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}
                      onClick={() => applyImageSelection(imgStr)}
                      title={imgStr}
                    >
                      <img src={getImageUrl(imgStr)} alt="premade" style={{ maxWidth: '100%', maxHeight: '100%' }} onError={(e) => e.target.style.display='none'} />
                    </div>
                  ))}
                </div>
              </div>
            </Tab>

            <Tab eventKey="gameicons" title="Game-Icons.net">
              <div className="px-3 pb-3 relative">
                <Form onSubmit={handleSearchGi} className="mb-3">
                  <InputGroup>
                    <Form.Control 
                      placeholder="Search for 'sword', 'magic', 'shield'..." 
                      value={giSearch} 
                      onChange={e => setGiSearch(e.target.value)} 
                    />
                    <Button type="submit" variant="primary" disabled={isSearchingGi}>
                      {isSearchingGi ? <Spinner size="sm" animation="border" /> : <FaSearch />} Search
                    </Button>
                  </InputGroup>
                </Form>

                <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                  {isDownloadingGi && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Spinner animation="border" variant="primary" />
                    </div>
                  )}

                  {!isSearchingGi && giResults.length === 0 && giSearch !== "" && (
                    <p className="text-muted w-100 text-center">No results found.</p>
                  )}

                  {giResults.map((iconStr, idx) => (
                    <div 
                      key={idx} 
                      className="border rounded p-2 text-center" 
                      style={{ width: '100px', cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                      onClick={() => handleGameIconSelect(iconStr)}
                      title={iconStr}
                    >
                      <img src={`https://api.iconify.design/${iconStr.replace(':', '/')}.svg`} alt="icon" style={{ width: '50px', height: '50px' }} />
                      <div className="small text-truncate mt-1" style={{ fontSize: '0.7rem' }}>{iconStr.split(':')[1]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Tab>

            <Tab eventKey="upload" title="Upload Custom SVG">
              <div className="px-3 pb-4 pt-2 text-center">
                <div className="p-5 border border-dashed rounded bg-light">
                  <h6 className="mb-3">Upload a New SVG File</h6>
                  <Form.Label className="btn btn-primary mb-0">
                    <FaUpload className="me-2" /> Choose SVG File
                    <Form.Control type="file" className="d-none" accept=".svg" onChange={handleFileUpload} />
                  </Form.Label>
                  <small className="d-block mt-2 text-muted">Only .svg format is allowed.</small>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>

      {/* All Suggestions Modal */}
      <Modal show={showSuggestionsModal} onHide={() => setShowSuggestionsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>All Winner Theme Suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {suggestions.length === 0 ? <p>No suggestions.</p> : (
            <ul className="list-group">
              {suggestions.map(s => (
                <li key={s.id} className="list-group-item d-flex justify-content-between align-items-start">
                  <div className="ms-2 me-auto">
                    <div className="fw-bold">{s.suggested_theme}</div>
                    <span className="text-muted small">Submitted: {s.created_at}</span>
                  </div>
                  <Badge bg="primary" pill>User ID: {s.winner_user_id}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>

    </Container>
  );
}